import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PageSEO } from "@/components/PageSEO";
import { SEO } from "@/lib/seo";
import {
  API, SERVICES, PROBLEMS, SOLUTIONS, METHOD_STEPS,
  TESTIMONIALS_DATA, LOCATIONS, SOCIAL_LINKS, WHATSAPP_NUMBER,
  CONTACT_EMAIL, TEAM_ITEMS
} from "@/lib/constants";
import {
  FileCheck, Compass, BarChart3, Phone, Mail, MapPin,
  Facebook, Instagram, Linkedin, Menu, X, Star, ArrowRight,
  CheckCircle, Users, Clock, Award, ChevronRight, MessageCircle,
  Building, Target, TrendingUp, Shield, Play, XCircle, Zap,
  Eye, Lock, AlertTriangle, Sparkles, ArrowDown, Edit, Trash2,
  Plus, Save, LogOut, Image, Download, ShoppingCart, FileSpreadsheet,
  HelpCircle, ChevronDown, CircleDollarSign, CalendarCheck, PieChart,
  FileText, Wallet, BadgePercent, Sparkle, RefreshCw, ShieldCheck, BookOpen
} from "lucide-react";

const MetodoPage = () => (
  <main className="pt-20" data-testid="metodo-page">
    <PageSEO {...SEO.metodo} />
    {/* Hero */}
    <section className="py-24 bg-[#FAF8F5]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-4xl">
          <p className="text-gold font-bold tracking-widest text-sm mb-4">IL NOSTRO APPROCCIO</p>
          <h1 className="font-heading text-6xl md:text-8xl text-[#0D1527] leading-none mb-6">
            IL METODO DEL<br /><span className="text-gold">CONTROLLO DIREZIONALE</span>
          </h1>
          <p className="text-xl text-[#4A5568] leading-relaxed mb-6">
            Se guardi il bilancio una volta l'anno e nel resto del tempo vai a intuito, 
            allora questo metodo e per te. Ti mettiamo in chiaro i numeri, senza tecnicismi. 
            Sai dove guadagni, dove perdi e quante tasse stai creando.
          </p>
          <p className="text-[#64708A]">
            Basta "lo vediamo a fine anno": ti facciamo leggere i numeri mentre sei in corsa.
          </p>
        </div>
      </div>
    </section>

    {/* Il Problema */}
    <section className="py-24 bg-[#F4EFEA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="font-heading text-5xl text-[#0D1527] mb-4">SE NON AFFRONTI QUESTA COSA</h2>
          <p className="text-[#4A5568]">I costi reali che paghi senza accorgertene</p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-netflix/10 border border-netflix/30 p-8 rounded-lg">
            <h3 className="font-heading text-2xl text-netflix mb-4">COSTO: SOLDI</h3>
            <p className="text-[#4A5568] text-sm leading-relaxed">
              Soldi che se ne vanno senza che tu te ne accorga. Continui a vendere un prodotto "che gira", 
              ma tra sconti, costi, resi e ore ti lascia quasi niente. Lo porti avanti perche "porta fatturato", 
              ma stai assorbendo una perdita.
            </p>
          </div>
          <div className="bg-netflix/10 border border-netflix/30 p-8 rounded-lg">
            <h3 className="font-heading text-2xl text-netflix mb-4">COSTO: TEMPO</h3>
            <p className="text-[#4A5568] text-sm leading-relaxed">
              Ore spese su clienti sbagliati, preventivi fatti a spanne, riunioni per lavori che rendono poco. 
              Quando a fine anno scopri che i numeri non tornano, e tardi: devi rincorrere e ti perdi opportunita vere.
            </p>
          </div>
          <div className="bg-netflix/10 border border-netflix/30 p-8 rounded-lg">
            <h3 className="font-heading text-2xl text-netflix mb-4">COSTO: STRESS</h3>
            <p className="text-[#4A5568] text-sm leading-relaxed">
              La sensazione di non avere il controllo e il timore delle "sorprese" a fine anno. 
              Ti ritrovi a decidere con l'ansia, non con i dati. Ogni scelta pesa di piu perche non sai 
              se stai facendo la cosa giusta.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* Steps */}
    <section className="py-24 bg-[#F4EFEA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <p className="text-gold font-bold tracking-widest text-sm mb-2">COME LO AFFRONTIAMO</p>
          <h2 className="font-heading text-5xl text-[#0D1527]">IL NOSTRO METODO IN 4 PASSI</h2>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {METHOD_STEPS.map((step, i) => (
            <div key={i} className="relative bg-white border border-[#E8E2D5] p-6 rounded-lg hover:border-gold/50 transition-all">
              <span className="font-heading text-6xl text-gold/30 absolute top-4 right-4">{step.num}</span>
              <h3 className="font-heading text-2xl text-[#0D1527] mb-4 relative z-10">{step.title.toUpperCase()}</h3>
              <p className="text-[#4A5568] text-sm relative z-10">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Cosa ti porti a casa */}
    <section className="py-24 bg-[#F4EFEA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="font-heading text-5xl text-[#0D1527] mb-4">COSA TI PORTI A CASA</h2>
          <p className="text-[#4A5568]">In concreto, ecco cosa ottieni</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          {[
            "Capisci in modo secco se stai facendo utile o solo 'movimento'",
            "Vedi dove perdi margine senza accorgertene: sconti, extra non fatturati, urgenze",
            "Capisci quali prodotti/servizi stanno ripagando davvero il tempo",
            "Individui 2-3 aree che possono erodere l'utile: costi ricorrenti, abbonamenti, forniture",
            "Capisci quali clienti ti conviene tenere e quali ti consumano ore",
            "Hai visibilita sull'impatto fiscale mentre l'anno va avanti",
            "Vedi dove stai lasciando margine: ore non conteggiate, extra, trasferte",
            "Hai un 'semaforo' sugli scostamenti: vedi prima quando qualcosa peggiora",
            "Ti porti a casa scelte pratiche: cosa spingere, cosa fermare, cosa sistemare",
            "Non ti resta 'tutto da capire': hai una sintesi chiara e i prossimi passi"
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-4 p-4 bg-[#FAF6EE] border border-gold/20 rounded-lg">
              <CheckCircle className="text-gold flex-shrink-0 mt-1" size={20} />
              <span className="text-[#3B4658]">{item}</span>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Scelta del livello */}
    <section className="py-24 bg-[#F4EFEA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="bg-gradient-to-r from-gold/10 to-transparent border border-gold/30 rounded-lg p-8 md:p-12">
          <div className="flex flex-col lg:flex-row gap-8 items-start">
            <div className="flex-1">
              <h2 className="font-heading text-4xl text-[#0D1527] mb-4">SCEGLI TU IL LIVELLO DI SERVIZIO</h2>
              <p className="text-[#4A5568] mb-6">
                Non ti obblighiamo a prendere tutto subito. Parti dal livello che ti serve oggi, 
                e puoi ampliare in qualsiasi momento. Il percorso si adatta alle tue esigenze:
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gold/20 rounded flex items-center justify-center flex-shrink-0">
                    <span className="text-gold font-bold">1</span>
                  </div>
                  <div>
                    <p className="text-[#0D1527] font-medium">Adempimenti</p>
                    <p className="text-[#64708A] text-sm">Contabilita, scadenze, F24 - la base per stare tranquillo</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gold/20 rounded flex items-center justify-center flex-shrink-0">
                    <span className="text-gold font-bold">2</span>
                  </div>
                  <div>
                    <p className="text-[#0D1527] font-medium">+ Controllo Periodico</p>
                    <p className="text-[#64708A] text-sm">Letture mensili/trimestrali dei numeri con sintesi e scelte pratiche</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-gold/20 rounded flex items-center justify-center flex-shrink-0">
                    <span className="text-gold font-bold">3</span>
                  </div>
                  <div>
                    <p className="text-[#0D1527] font-medium">+ Consulenza Strategica</p>
                    <p className="text-[#64708A] text-sm">Analisi margini, pricing, mix vendita, affiancamento decisionale</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-netflix/20 rounded flex items-center justify-center flex-shrink-0">
                    <span className="text-netflix font-bold">4</span>
                  </div>
                  <div>
                    <p className="text-[#0D1527] font-medium">+ Pannello Direzionale</p>
                    <p className="text-[#64708A] text-sm">Dashboard, KPI real-time, riunioni guidate, controllo totale</p>
                  </div>
                </div>
              </div>
              <p className="text-gold text-sm mt-6 font-medium">
                Puoi salire di livello in qualsiasi momento. Nessun vincolo.
              </p>
            </div>
            <div className="lg:w-auto">
              <Link to="/contatti" className="inline-flex items-center gap-3 bg-gold text-black hover:bg-[#96742F] px-8 py-4 rounded font-bold tracking-wide transition-all hover:scale-105 whitespace-nowrap">
                SCOPRI IL LIVELLO GIUSTO <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Transformation */}
    <section className="py-24 bg-[#F4EFEA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="font-heading text-5xl text-[#0D1527] mb-4">LA TRASFORMAZIONE</h2>
          <p className="text-[#4A5568]">Da confusione a controllo totale</p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <h3 className="font-heading text-3xl text-netflix flex items-center gap-3">
              <XCircle /> PRIMA
            </h3>
            {[
              "Bilanci incomprensibili",
              "Tasse a sorpresa",
              "Decisioni 'a naso'",
              "Margini che non tornano",
              "Clienti che consumano ore senza rendere"
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 text-[#4A5568]">
                <div className="w-2 h-2 bg-netflix rounded-full" />
                {item}
              </div>
            ))}
          </div>
          
          <div className="space-y-6">
            <h3 className="font-heading text-3xl text-gold flex items-center gap-3">
              <CheckCircle /> DOPO
            </h3>
            {[
              "Numeri chiari ogni mese",
              "Tasse pianificate con anticipo",
              "Decisioni basate sui dati",
              "Sai cosa rende e cosa no",
              "Piu controllo, meno stress"
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-4 text-[#2A3446]">
                <div className="w-2 h-2 bg-gold rounded-full" />
                {item}
              </div>
            ))}
          </div>
        </div>
        
        <div className="text-center mt-16">
          <Link
            to="/contatti"
            className="inline-flex items-center gap-3 bg-gold text-black hover:bg-[#96742F] px-10 py-5 rounded font-bold text-lg tracking-wide transition-all hover:scale-105"
          >
            INIZIA LA TUA TRASFORMAZIONE <ArrowRight size={20} />
          </Link>
        </div>
      </div>
    </section>

    {/* Garanzia */}
    <section className="py-24 bg-[#F4EFEA]">
      <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
        <div className="bg-white border border-[#E8E2D5] rounded-lg p-8 md:p-12">
          <h2 className="font-heading text-4xl text-[#0D1527] mb-4">GARANZIA "PAGHI SOLO CIO CHE FACCIAMO"</h2>
          <p className="text-[#4A5568] mb-6">
            Se nelle prime settimane ci rendiamo conto che questo percorso non e adatto alla tua azienda 
            (o che non e il momento giusto per partire), chiudiamo l'incarico in modo ordinato.
          </p>
          <p className="text-[#64708A]">
            Ti viene fatturato solo il lavoro effettivamente svolto fino a quel momento, 
            senza costi aggiuntivi o voci "a sorpresa".
          </p>
        </div>
      </div>
    </section>
  </main>
);


export { MetodoPage };
